/**
 * Toast Container Component
 * Displays toast notifications
 */

import React, { useState, useEffect } from 'react';
import { toast, Toast } from '../../utils/toast';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes } from 'react-icons/fa';

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = toast.subscribe(setToasts);
    return () => {
      unsubscribe();
    };
  }, []);

  const getToastIcon = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return <FaCheckCircle className="text-green-500" />;
      case 'error':
        return <FaExclamationCircle className="text-red-500" />;
      case 'warning':
        return <FaExclamationCircle className="text-yellow-500" />;
      case 'info':
        return <FaInfoCircle className="text-blue-500" />;
    }
  };

  const getToastStyles = (type: Toast['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`flex items-center gap-3 p-4 rounded-lg border shadow-lg animate-slide-in ${getToastStyles(
            t.type
          )}`}
        >
          <div className="shrink-0">{getToastIcon(t.type)}</div>
          <p className="flex-1 text-sm text-gray-800">{t.message}</p>
          <button
            onClick={() => toast.remove(t.id)}
            className="shrink-0 text-gray-500 hover:text-gray-700"
          >
            <FaTimes />
          </button>
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
